import React, { useState, useEffect } from 'react';
import { MatchPlay, MatchPlayHole, HoleResult } from '../../types/matchplay';
import { GolfCourse } from '../../types';
import { ChevronLeft, Save, Flag } from 'lucide-react';

interface MatchPlayScoringProps {
  match: MatchPlay;
  course: GolfCourse;
  onScoreSubmit: (hole: number, result: MatchPlayHole) => void;
  onMatchComplete: (match: MatchPlay) => void;
}

export function MatchPlayScoring({ match, course, onScoreSubmit, onMatchComplete }: MatchPlayScoringProps) {
  const [currentHole, setCurrentHole] = useState(() => {
    const playedHoles = match.holes.map(h => h.number);
    for (let i = match.startingHole || 1; i <= 18; i++) {
      if (!playedHoles.includes(i)) {
        return i;
      }
    }
    for (let i = 1; i < (match.startingHole || 1); i++) {
      if (!playedHoles.includes(i)) {
        return i;
      }
    }
    return match.holes.length > 0 ? match.holes[match.holes.length - 1].number : (match.startingHole || 1);
  });
  
  const [team1Score, setTeam1Score] = useState<number | ''>('');
  const [team2Score, setTeam2Score] = useState<number | ''>('');

  // Initialize scores with par values
  useEffect(() => {
    setTeam1Score(course.parValues[currentHole - 1]);
    setTeam2Score(course.parValues[currentHole - 1]);
  }, [currentHole, course.parValues]);

  useEffect(() => {
    const playedHoles = match.holes.map(h => h.number);
    for (let i = match.startingHole || 1; i <= 18; i++) {
      if (!playedHoles.includes(i)) {
        setCurrentHole(i);
        return;
      }
    }
    for (let i = 1; i < (match.startingHole || 1); i++) {
      if (!playedHoles.includes(i)) {
        setCurrentHole(i);
        return;
      }
    }
    if (match.holes.length > 0) {
      setCurrentHole(match.holes[match.holes.length - 1].number);
    }
  }, [match.id, match.holes, match.startingHole]);

  const calculateHoleResult = (): HoleResult => {
    if (team1Score === '' || team2Score === '') return 'halved';
    if (team1Score < team2Score) return 'team1';
    if (team2Score < team1Score) return 'team2';
    return 'halved';
  };

  const findNextUnplayedHole = (startingFrom: number): number => {
    const playedHoles = match.holes.map(h => h.number);
    for (let i = startingFrom + 1; i <= 18; i++) {
      if (!playedHoles.includes(i)) {
        return i;
      }
    }
    for (let i = 1; i <= startingFrom; i++) {
      if (!playedHoles.includes(i)) {
        return i;
      }
    }
    return startingFrom;
  };

  const handleScoreSubmit = () => {
    if (team1Score === '' || team2Score === '') return;

    const result: MatchPlayHole = {
      number: currentHole,
      winner: calculateHoleResult(),
      team1Strokes: [team1Score],
      team2Strokes: [team2Score]
    };

    onScoreSubmit(currentHole, result);
    
    setTeam1Score('');
    setTeam2Score('');
    
    const updatedHoles = [...match.holes];
    const existingHoleIndex = updatedHoles.findIndex(h => h.number === currentHole);
    if (existingHoleIndex >= 0) {
      updatedHoles[existingHoleIndex] = result;
    } else {
      updatedHoles.push(result);
    }

    const team1Wins = updatedHoles.filter(h => h.winner === 'team1').length;
    const team2Wins = updatedHoles.filter(h => h.winner === 'team2').length;
    const holesRemaining = 18 - updatedHoles.length;
    const lead = team1Wins - team2Wins;
    const absLead = Math.abs(lead);

    // Check for match completion conditions
    if (
      updatedHoles.length === 18 || // All holes played
      absLead > holesRemaining || // Mathematically impossible to catch up
      (holesRemaining <= 2 && absLead >= 3) // 3 up with 2 or fewer to play
    ) {
      // Format result string
      const resultString = lead === 0 
        ? 'AS' 
        : `${absLead}&${holesRemaining}`;

      // Complete the match
      onMatchComplete({
        ...match,
        holes: updatedHoles,
        status: 'complete',
        result: resultString
      });
    } else {
      const nextHole = findNextUnplayedHole(currentHole);
      setCurrentHole(nextHole);
    }
  };

  const getMatchStatus = () => {
    const holesPlayed = match.holes.length;
    const holesRemaining = 18 - holesPlayed;
    let team1Score = 0;

    match.holes.forEach(hole => {
      if (hole.winner === 'team1') team1Score++;
      else if (hole.winner === 'team2') team1Score--;
    });

    if (team1Score === 0) return 'AS';
    
    const lead = Math.abs(team1Score);
    if (lead > holesRemaining) {
      return `${lead}&${holesRemaining}`;
    }
    return `${lead}${team1Score > 0 ? 'U' : 'D'}`;
  };

  const getHoleStatus = (holeNumber: number) => {
    let team1Score = 0;
    for (const hole of match.holes) {
      if (hole.number > holeNumber) break;
      if (hole.winner === 'team1') team1Score++;
      else if (hole.winner === 'team2') team1Score--;
    }

    if (team1Score === 0) return 'AS';
    const lead = Math.abs(team1Score);
    return `${lead}${team1Score > 0 ? 'U' : 'D'}`;
  };

  const getActivePlayer = (team: 'team1' | 'team2') => {
    if (match.format !== 'foursomes') return null;
    const playerIndex = (currentHole - 1) % 2;
    return team === 'team1' ? match.team1.players[playerIndex] : match.team2.players[playerIndex];
  };

  const formatTeamNames = (team: typeof match.team1) => {
    return team.players.map(p => p.name).join('/');
  };

  const team1ActivePlayer = getActivePlayer('team1');
  const team2ActivePlayer = getActivePlayer('team2');

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <button 
          onClick={() => onMatchComplete(match)}
          className="text-gray-600 hover:text-gray-800"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="text-center">
          <h2 className="text-xl font-semibold">Hole {currentHole}</h2>
          <div className="text-sm text-gray-600">
            Par {course.parValues[currentHole - 1]}
          </div>
          <div className="text-sm font-medium mt-1">
            {getMatchStatus()}
          </div>
        </div>
        <div className="w-6" />
      </div>

      <div className="space-y-6">
        {/* Team Scoring */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Team 1 Scoring */}
          <div className="space-y-4">
          <h3 className="font-medium mb-2">
            {formatTeamNames(match.team1)}
            {team1ActivePlayer && (
              <span className="text-sm text-gray-600 ml-2">
                ({team1ActivePlayer.name}'s shot)
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const currentScore = team1Score || course.parValues[currentHole - 1];
                if (currentScore > 1) {
                  setTeam1Score(currentScore - 1);
                }
              }}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              type="button"
            >
              -
            </button>
            <div className="w-16 text-center">
              {team1Score ? (
                <div>
                  <div className="font-medium">
                    {team1Score}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(() => {
                      const relation = team1Score - course.parValues[currentHole - 1];
                      if (relation > 0) return `(+${relation})`;
                      if (relation < 0) return `(${relation})`;
                      return '(E)';
                    })()}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-medium text-gray-400">
                    {course.parValues[currentHole - 1]}
                  </div>
                  <div className="text-xs text-gray-400">Par</div>
                </div>
              )}
            </div>
            <button
              onClick={() => {
                const currentScore = team1Score || course.parValues[currentHole - 1];
                setTeam1Score(currentScore + 1);
              }}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              type="button"
            >
              +
            </button>
          </div>
          </div>

          {/* Team 2 Scoring */}
          <div className="space-y-4">
          <h3 className="font-medium mb-2">
            {formatTeamNames(match.team2)}
            {team2ActivePlayer && (
              <span className="text-sm text-gray-600 ml-2">
                ({team2ActivePlayer.name}'s shot)
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const currentScore = team2Score || course.parValues[currentHole - 1];
                if (currentScore > 1) {
                  setTeam2Score(currentScore - 1);
                }
              }}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              type="button"
            >
              -
            </button>
            <div className="w-16 text-center">
              {team2Score ? (
                <div>
                  <div className="font-medium">
                    {team2Score}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(() => {
                      const relation = team2Score - course.parValues[currentHole - 1];
                      if (relation > 0) return `(+${relation})`;
                      if (relation < 0) return `(${relation})`;
                      return '(E)';
                    })()}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-medium text-gray-400">
                    {course.parValues[currentHole - 1]}
                  </div>
                  <div className="text-xs text-gray-400">Par</div>
                </div>
              )}
            </div>
            <button
              onClick={() => {
                const currentScore = team2Score || course.parValues[currentHole - 1];
                setTeam2Score(currentScore + 1);
              }}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              type="button"
            >
              +
            </button>
          </div>
          </div>
        </div>

        <button
          onClick={handleScoreSubmit}
          disabled={false}
          className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <Save className="w-5 h-5" />
          Save Hole Result
        </button>
      </div>

      {/* Match Progress */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="font-medium mb-4">Match Progress</h3>
        <div className="grid grid-cols-9 gap-1">
          {[...Array(18)].map((_, index) => {
            const hole = match.holes.find(h => h.number === index + 1);
            const status = getHoleStatus(index + 1);

            return (
              <div
                key={index}
                className={`p-2 text-center rounded ${
                  hole
                    ? index + 1 === currentHole
                      ? 'bg-primary-100 text-primary-800 ring-2 ring-primary-500'
                      : hole.winner === 'team1'
                      ? 'bg-green-100 text-green-800'
                      : hole.winner === 'team2'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                    : 'bg-gray-50 text-gray-400'
                }`}
                onClick={() => {
                  // Allow clicking to edit previous holes
                  if (hole) {
                    setCurrentHole(index + 1);
                    setTeam1Score(hole.team1Strokes[0]);
                    setTeam2Score(hole.team2Strokes[0]);
                  }
                }}
                style={{ cursor: hole ? 'pointer' : 'default' }}
              >
                <div className="text-xs">#{index + 1}</div>
                <div className="font-medium">
                  {hole ? status : '-'}
                </div>
                {hole && (
                  <div className="text-xs mt-1">
                    {hole.team1Strokes[0]} - {hole.team2Strokes[0]}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}